import { UseMutationOptions, useQueryClient } from '@tanstack/react-query';

import useMoveProposalGroupOrLineItem from '@/api/mutations/proposals/useMoveProposalGroupOrLineItem';
import proposalKeys from '@/api/queries/proposal/keyFactor';
import {
  MoveProposalGroupOrLineItemInput,
  MoveProposalGroupOrLineItemMutation,
} from '@/gql/graphql';

import { applyProposalGroupOrLineItemMove } from './applyProposalGroupOrLineItemMove';
import { createOptimisticProposalMutationOptions } from './createOptimisticProposalMutation';

/**
 * Optimistic wrapper around {@link useMoveProposalGroupOrLineItem}: applies
 * the move to the cached proposal in `onMutate` so the tree holds the drop
 * immediately, then rolls back on error — the restored cache recomputes the
 * SortableTree's items, snapping the drop back. The base hook invalidates the
 * proposal on success, reconciling with server truth.
 */
const useOptimisticMoveProposalGroupOrLineItem = (args: {
  proposalId: string;
  options?: UseMutationOptions<
    MoveProposalGroupOrLineItemMutation,
    unknown,
    MoveProposalGroupOrLineItemInput
  >;
}) => {
  const { proposalId, options } = args;
  const queryClient = useQueryClient();
  const queryKey = proposalKeys.proposal(proposalId);

  return useMoveProposalGroupOrLineItem({
    proposalId,
    options: {
      ...options,
      ...createOptimisticProposalMutationOptions({
        queryClient,
        queryKey,
        apply: applyProposalGroupOrLineItemMove,
        options,
      }),
    },
  });
};

export default useOptimisticMoveProposalGroupOrLineItem;
